import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {
    getSavedListsCountsFailure,
    getSavedListsCountsRequest,
    getSavedListsCountsSuccess,
} from 'src/openapi/admin/apis/UserContentCountApi';

type ListsState = {
    total: number,
    status: 'idle' | 'loading' | 'succeeded' | 'failed',
    error?: string,
    receivedAt?: number,
};

export type UserPageState = {
    userPage: {
        tabs: {
            content: {
                lists: ListsSliceState,
            },
        },
    },
};

type ListsSliceState = {
    countsByUserId: Record<number, ListsState>,
};

const initialState: ListsSliceState = {
    countsByUserId: {},
};

const computeTotal = (data: Record<string, number | null | undefined>): number => {
    if (!data) {
        return 0;
    }

    const values = Object.values(data) as Array<number | null | undefined>;
    return values.reduce((sum: number, val) => sum + (val ?? 0), 0);
};

export const listsSlice = createSlice({
    name: 'lists',
    initialState,
    reducers: {
        invalidateUserLists: (state, action: PayloadAction<number>) => {
            const userId = action.payload;
            const userLists = state.countsByUserId[userId];
            if (userLists) {
                userLists.status = 'idle';
                userLists.receivedAt = undefined;
            }
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getSavedListsCountsRequest, (state, action) => {
                const {userId} = action.payload.meta;
                if (!state.countsByUserId[userId]) {
                    state.countsByUserId[userId] = {
                        total: 0,
                        status: 'loading',
                    };
                } else {
                    state.countsByUserId[userId].status = 'loading';
                }
            })
            .addCase(getSavedListsCountsSuccess, (state, action) => {
                const {userId} = action.payload.meta;
                const data = action.payload.data;
                const total = computeTotal(data);

                state.countsByUserId[userId] = {
                    total,
                    status: 'succeeded',
                    receivedAt: Date.now(),
                };
            })
            .addCase(getSavedListsCountsFailure, (state, action) => {
                const {userId} = action.payload.meta;
                state.countsByUserId[userId] = {
                    total: 0,
                    status: 'failed',
                    error: action.payload.data?.message ?? 'Unknown error',
                };
            });
    },
});

export const {invalidateUserLists} = listsSlice.actions;
